import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import { useAuth } from '../../hooks/useAuth'

const ISSUE_AREAS = ['Collection', 'Product', 'Deal', 'Order', 'Payment', 'Other']

function PinButton({ entityType, entityId, entityName, onPinned, className = '' }) {
  const { user } = useAuth()
  const [showModal, setShowModal] = useState(false)
  const [issueArea, setIssueArea] = useState('')
  const [reason, setReason] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [users, setUsers] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function openModal() {
    setShowModal(true)
    setIssueArea('')
    setReason('')
    setDueDate('')
    setAssignedTo('')
    setError('')
    if (user?.is_admin) {
      loadUsers()
    }
  }

  async function loadUsers() {
    try {
      const response = await api.get('/api/users')
      setUsers(Array.isArray(response) ? response : (response.users || response.data || []))
    } catch (err) {
      console.error('Failed to load users:', err)
    }
  }

  function closeModal() {
    setShowModal(false)
    setError('')
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!issueArea) {
      setError('Please select an issue area')
      return
    }
    if (!reason.trim()) {
      setError('Please enter what to consider')
      return
    }

    setSaving(true)
    setError('')

    try {
      const body = {
        entityType,
        entityId: String(entityId),
        issueArea,
        reason: reason.trim(),
        dueDate: dueDate || null
      }
      if (assignedTo) {
        body.assignedTo = Number(assignedTo)
      }
      await api.post('/api/pinned', body)

      closeModal()
      if (onPinned) {
        onPinned()
      }
    } catch (err) {
      if (err.message?.includes('already pinned')) {
        setError('This item is already pinned')
      } else {
        setError(err.message || 'Failed to pin item')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <button
        className={`btn btn-sm btn-accent ${className}`}
        onClick={openModal}
        title={`Pin this ${entityType}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        Pin
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content pin-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Pin {entityType.charAt(0).toUpperCase() + entityType.slice(1)}</h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {entityName && (
                  <div style={{ marginBottom: 'var(--space-4)', padding: 'var(--space-3)', background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)' }}>
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginBottom: 2 }}>Item</div>
                    <div style={{ fontWeight: 500 }}>{entityName}</div>
                  </div>
                )}

                {error && (
                  <div style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-2) var(--space-3)', background: '#fee2e2', color: '#991b1b', borderRadius: 'var(--radius)', fontSize: '0.875rem' }}>
                    {error}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Issue Area *</label>
                  <select
                    className="form-input"
                    value={issueArea}
                    onChange={e => setIssueArea(e.target.value)}
                    required
                  >
                    <option value="">Select area...</option>
                    {ISSUE_AREAS.map(area => (
                      <option key={area} value={area}>{area}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">What to Consider *</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder="Describe the issue or what needs attention..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Due By Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={dueDate}
                    onChange={e => setDueDate(e.target.value)}
                  />
                </div>

                {user?.is_admin && users.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Assign To</label>
                    <select
                      className="form-input"
                      value={assignedTo}
                      onChange={e => setAssignedTo(e.target.value)}
                    >
                      <option value="">Myself (default)</option>
                      {users.map(u => (
                        <option key={u.user_id} value={u.user_id}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Pinning...' : 'Pin Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}

export default PinButton
